import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import AppShell from "./layouts/AppShell";
import SignIn from "./pages/SignIn";
import AcceptInvite from "./pages/AcceptInvite";
import Landing from "./pages/Landing";
import { useAuth } from "./lib/auth";

// Code-split the heavy interactive pages. ScholarPath, EvidenceCriteria,
// LetterStudio, and I140Intake are each ~1k–3k lines of @ts-nocheck
// React with their own inlined design CSS. Eagerly importing all of
// them inflated the first paint of /sign-in and the marketing landing
// with code those screens never use. lazy() puts each behind its own
// chunk that only loads when the user navigates to it.
const Applications = lazy(() => import("./pages/Applications"));
const ApplicationDetail = lazy(() => import("./pages/ApplicationDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Engagements = lazy(() => import("./pages/Engagements"));
const EngagementWorkspacePage = lazy(() => import("./pages/EngagementWorkspace"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Publications = lazy(() => import("./pages/Publications"));
const Recommenders = lazy(() => import("./pages/Recommenders"));
const ScholarImport = lazy(() => import("./pages/ScholarImport"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ScholarPathGuided = lazy(() => import("./pages/ScholarPath"));
const I140Intake = lazy(() => import("./pages/I140Intake"));
const LetterStudio = lazy(() => import("./pages/LetterStudio"));
const DocumentPortal = lazy(() => import("./pages/DocumentPortal"));
const LetterUpload = lazy(() => import("./pages/LetterUpload"));
const ReviewBeforeCheckout = lazy(() => import("./pages/ReviewBeforeCheckout"));
const PostCheckout = lazy(() => import("./pages/PostCheckout"));
const Refer = lazy(() => import("./pages/Refer"));
const SharedView = lazy(() => import("./pages/SharedView"));
const Expert = lazy(() => import("./pages/Expert"));
const PetitionBrief = lazy(() => import("./pages/PetitionBrief"));
const AttorneyReview = lazy(() => import("./pages/AttorneyReview"));
const AttorneyPortal = lazy(() => import("./pages/AttorneyPortal"));
const EvidenceCriteria = lazy(() => import("./pages/EvidenceCriteria"));
const CriticalRoleLetter = lazy(() => import("./pages/CriticalRoleLetter"));

// Lightweight loading fallback — matches the warm palette so the user
// doesn't see a flash of white between routes.
function RouteLoading() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#ece2cf",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        color: "#5c574b",
        fontSize: 14,
      }}
    >
      Loading…
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

function RedirectIfSignedIn({
  children,
  to,
}: {
  children: ReactNode;
  to: string;
}) {
  const { session } = useAuth();
  if (session) return <Navigate to={to} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<RouteLoading />}>
    <Routes>
      {/* Public marketing landing — design handoff's front door. Signed-in
          users get bounced into the app at /scholar-path. */}
      <Route
        path="/"
        element={
          <RedirectIfSignedIn to="/scholar-path">
            <Landing />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/sign-in"
        element={
          <RedirectIfSignedIn to="/scholar-path">
            <SignIn />
          </RedirectIfSignedIn>
        }
      />
      {/* Marketing landing CTAs ("Start my case", "Free assessment") link to
          app.visagenius.io/start. Route it to the in-app conversion flow so a
          bookmarked /start (or a stale email) still works. */}
      <Route path="/start" element={<Navigate to="/scholar-path" replace />} />
      <Route path="/auth/accept-invite" element={<AcceptInvite />} />
      {/* Public, unauthenticated — recommender magic-link landing page. */}
      <Route path="/letter-upload/:token" element={<LetterUpload />} />
      {/* Public, unauthenticated — attorney share-link landing page. */}
      <Route path="/share/:token" element={<SharedView />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/app" element={<Navigate to="/scholar-path" replace />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/intake" element={<ScholarImport />} />
        <Route path="/publications" element={<Publications />} />
        <Route path="/recommenders" element={<Recommenders />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route
          path="/engagements/:engagementId"
          element={<EngagementWorkspacePage />}
        />
      </Route>

      {/* Scholar Path (Guided) — the card-based conversion flow. Sign-in is
          the front door of the product, so this route is auth-gated. The
          SignIn.verifyCode handler routes new users (no applications yet)
          to step 1 (EligibilityScholar — Google Scholar URL prompt) and
          returning users straight to Case Plan (step 5 or 6). Letter
          Studio stays public so the Case Plan tile ("Recommendation
          letters") still works as a guest preview, and the marketing
          Landing page at "/" remains public. */}
      <Route
        path="/scholar-path"
        element={
          <ProtectedRoute>
            <ScholarPathGuided />
          </ProtectedRoute>
        }
      />

      <Route
        path="/i140-intake"
        element={
          <ProtectedRoute>
            <I140Intake />
          </ProtectedRoute>
        }
      />

      <Route
        path="/letter-studio"
        element={<LetterStudio />}
      />

      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentPortal />
          </ProtectedRoute>
        }
      />

      <Route
        path="/review/:applicationId"
        element={
          <ProtectedRoute>
            <ReviewBeforeCheckout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/post-checkout"
        element={
          <ProtectedRoute>
            <PostCheckout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/refer"
        element={
          <ProtectedRoute>
            <Refer />
          </ProtectedRoute>
        }
      />

      <Route path="/expert" element={<Expert />} />

      <Route
        path="/petition-brief"
        element={
          <ProtectedRoute>
            <PetitionBrief />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attorney-review"
        element={
          <ProtectedRoute>
            <AttorneyReview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attorney-portal"
        element={
          <ProtectedRoute>
            <AttorneyPortal />
          </ProtectedRoute>
        }
      />

      <Route path="/evidence" element={<EvidenceCriteria />} />

      <Route
        path="/critical-role-letter"
        element={
          <ProtectedRoute>
            <CriticalRoleLetter />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
